import {useState} from 'react';
import {useAuthStore} from '@/store/auth_store';
import {useMessageThreadInbox} from '@/hooks/useMessageThreadInbox';
import {useDebounce} from '@/hooks/useDebounce';
import WidgetPage from '@common/widgets/WidgetPage';
import {WidgetHeaderLeft} from '@common/widgets/WidgetPage.styles';
import {FilterBar, FilterTabs, FilterChip} from '@/components/pages/messages/MessagesFilter.styles';
import WidgetOnlyShowUnread from '@common/widgets/WidgetOnlyShowUnread';
import MessageThreadItem from '@/components/pages/messages/MessageThreadItem';
import SearchBarComponent from '@common/SearchBarComponent';

enum FilterTab {
  ALL = 'All',
  CLIENT = 'Client',
  TASK = 'Task',
  REFERRAL = 'Referral',
}

const TABS: FilterTab[] = [FilterTab.ALL, FilterTab.CLIENT, FilterTab.TASK, FilterTab.REFERRAL];

export default function MessagesPage() {
  const currentUser = useAuthStore((state) => state.currentUser);
  const [activeFilter, setActiveFilter] = useState<FilterTab>(FilterTab.ALL);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);

  const {
    isLoading,
    isApiLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching,
    error,
    messageThreadSummaries,
    messageThreadSummariesSearched,
    totalUnreadMessages,
    markAllAsRead,
  } = useMessageThreadInbox({
    onlyShowUnreadMessages: currentUser?.only_show_unread_messages ?? false,
    searchTerm: debouncedSearchTerm,
    threadType: activeFilter === FilterTab.ALL ? undefined : activeFilter,
  });

  const baseList = messageThreadSummariesSearched ?? messageThreadSummaries;

  return (
    <WidgetPage
      elements={baseList}
      totalUnreadElements={totalUnreadMessages}
      dateProperty="latestMessage.createdAt"
      title="Messages"
      isLoading={isLoading}
      isApiLoading={isApiLoading}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetching={isFetching}
      isRefetching={isRefetching}
      error={error}
      markAllAsRead={markAllAsRead}
      render={(messageThreadSummary) => (
        <MessageThreadItem key={messageThreadSummary.id} messageThreadSummary={messageThreadSummary} />
      )}
      filterBar={
        <FilterBar>
          <FilterTabs>
            {TABS.map(tab => (
              <FilterChip key={tab} active={activeFilter === tab} onClick={() => setActiveFilter(tab)}>
                {tab}
              </FilterChip>
            ))}
          </FilterTabs>
        </FilterBar>
      }
    >
      <WidgetHeaderLeft>
        <SearchBarComponent
          searchPlaceholder="Search"
          searchTerm={searchTerm}
          searchFunction={setSearchTerm}
          searchIcon
          concise
        />
      </WidgetHeaderLeft>
      {currentUser && (
        <WidgetOnlyShowUnread
          queryKey={(u) => ['messageThreadInbox', u.only_show_unread_messages]}
          currentFetchedUser={currentUser}
          property="only_show_unread_messages"
          label="Messages"
        />
      )}
    </WidgetPage>
  );
}
